import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { InfoTip } from "./InfoTip";
import { getAllSegmentDefs } from "../lib/segmentation";
import { useSegmentsStatus } from "../lib/useSegmentsStatus";

// بانِي الجمهور الموحَّد (2026-10) — مكوّن واجهة واحد مشترك تستدعيه شاشات
// الاستبيانات/الجماعية/المجدولة. يبني تعريف جمهور، يعاين العدّ/التغطية/
// الكلفة عبر /audience-builder/preview، ثم يمرّر قائمة الجوالات النهائية
// للشاشة الأم عبر onApply — لا يلمس مسار الإرسال نفسه.
// 2026-10 (بند 10) — الرموز هنا هي رموز RFM الحقيقية (routes/segments.py
// ::_bulk_classify_paid_mode) المطابقة لما ترسله كل الشاشات فعليًا؛ القائمة
// السابقة (vip/atrisk/lapsed/hibernating/bigspender...) لم تكن تطابق أي شيء
// حقيقي في الخلفية بعد إصلاح services/audience_builder.py لهذا البند.
// A3 (البند 3ب) — دالة لا ثابت: التسميات من تعريفات الخادم (getAllSegmentDefs)
// تُستدعى عند الريندر كي تلتقط التعريفات بعد اكتمال تحميلها.
const getSegments = () => [
  { id: "all", label: "الكل" },
  ...getAllSegmentDefs().map((d) => ({ id: d.code, label: d.label })),
];

const GENDERS = [
  { id: "", label: "بلا فلتر" },
  { id: "female", label: "نساء" },
  { id: "male", label: "رجال" },
  { id: "unknown", label: "غير معروف" },
];

export function AudienceBuilderPanel({ screenName, onApply, fixedSegment, externalSegments }) {
  useSegmentsStatus(); // إعادة رسم بعد اكتمال تحميل تعريفات الشرائح
  // جولة تصحيح 2026-10-13 (بند 3ج) — «سجّلوا ولم يزوروا» نوع جمهور مستقل
  // تماماً عن محرّك الشرائح/الفرع (مصدره datasets.cust لا pos_sales)،
  // فلا يتحد مع بقية الفلاتر أدناه؛ اختياره يُخفيها.
  const [audienceMode, setAudienceMode] = useState("segment"); // segment | registered_no_visit
  const [segment, setSegment] = useState(fixedSegment || "all");
  const [gender, setGender] = useState("");
  const [ageMin, setAgeMin] = useState("");
  const [ageMax, setAgeMax] = useState("");
  const [includeUnknown, setIncludeUnknown] = useState(false);
  const [productId, setProductId] = useState("");
  const [productDays, setProductDays] = useState(90);
  const [branchId, setBranchId] = useState("");
  const [minVisits, setMinVisits] = useState("");
  const [metricState, setMetricState] = useState("");
  const [metricStates, setMetricStates] = useState([]);
  const [options, setOptions] = useState({ products: [], branches: [] });
  const [preview, setPreview] = useState(null); // {loading, data, error}
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/audience-builder/options").then(({ data }) => setOptions(data))
      .catch(() => {});
    // «البوابة قبل النشر» 2026-10-13 (بند 2ب) — نفس قاموس الحالات الجاهزة
    // الخمس الذي تفتحه أزرار مركز العملاء وقائمة العملاء الموحَّدة.
    api.get("/customers/unified-list/metric-states").then(({ data }) => setMetricStates(data.states || []))
      .catch(() => {});
  }, []);

  const buildDefinition = () => {
    if (audienceMode === "registered_no_visit") return { kind: "registered_no_visit" };
    const def = externalSegments ? { segments: externalSegments } : { segment };
    if (gender) def.gender = gender;
    if (ageMin !== "") def.age_min = Number(ageMin);
    if (ageMax !== "") def.age_max = Number(ageMax);
    if (includeUnknown) def.include_unknown = true;
    if (productId) def.product = { product_id: productId, days: Number(productDays) || 90 };
    if (branchId) def.branch_id = branchId;
    if (minVisits) def.min_visits = Number(minVisits);
    if (metricState) def.metric_state = metricState;
    return def;
  };

  const runPreview = async () => {
    if (externalSegments && externalSegments.length === 0) {
      setPreview({ loading: false, data: null, error: "اختر شريحة واحدة على الأقل" });
      return;
    }
    setPreview({ loading: true, data: null, error: null });
    try {
      const { data } = await api.post("/audience-builder/preview", buildDefinition());
      setPreview({ loading: false, data, error: null });
    } catch (e) {
      setPreview({ loading: false, data: null, error: e?.response?.data?.detail || "فشلت المعاينة" });
    }
  };

  const apply = async () => {
    if (!preview?.data) return;
    try {
      await api.post("/audience-builder/record-use", {
        screen: screenName, definition: buildDefinition(), counts: preview.data.counts,
      });
    } catch (e) { /* الحوكمة best-effort — لا تمنع الاستخدام */ }
    onApply(preview.data.phones, buildDefinition(), preview.data.counts, preview.data);
  };

  return (
    <div data-testid="audience-builder-panel" className="rounded-xl p-4 border-2"
         style={{ borderColor: "#C4B5FD", background: "#F5F3FF", marginTop: 12 }}>
      <button type="button" data-testid="audience-builder-toggle"
              onClick={() => setOpen((o) => !o)}
              className="text-sm font-bold mb-2" style={{ color: "#5B21B6", background: "none", border: "none", cursor: "pointer" }}>
        🧩 بانِي الجمهور الموحَّد {open ? "▲" : "▼"}
      </button>
      {open && (
        <div>
          <div className="text-xs font-bold mb-1" style={{ color: "#5B21B6" }}>نوع الجمهور:</div>
          <div className="flex gap-2 mb-3">
            <button type="button" data-testid="ab-mode-segment"
                    onClick={() => setAudienceMode("segment")}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold border-2"
                    style={{ borderColor: audienceMode === "segment" ? "#7C3AED" : "#e5e7eb",
                             background: audienceMode === "segment" ? "#EDE9FE" : "white",
                             color: audienceMode === "segment" ? "#5B21B6" : "#475569" }}>
              فلتر الشرائح/الحالات
            </button>
            <button type="button" data-testid="ab-mode-registered-no-visit"
                    onClick={() => setAudienceMode("registered_no_visit")}
                    className="px-3 py-1.5 rounded-lg text-xs font-bold border-2"
                    style={{ borderColor: audienceMode === "registered_no_visit" ? "#7C3AED" : "#e5e7eb",
                             background: audienceMode === "registered_no_visit" ? "#EDE9FE" : "white",
                             color: audienceMode === "registered_no_visit" ? "#5B21B6" : "#475569" }}>
              سجّلوا ولم يزوروا
            </button>
          </div>

          {audienceMode === "segment" && (<>
          <div className="text-xs font-bold mb-1" style={{ color: "#5B21B6" }}>الشريحة:</div>
          {externalSegments ? (
            <div data-testid="ab-segment-external" className="w-full text-xs p-2 rounded-lg border mb-3 bg-gray-50 text-gray-600">
              {externalSegments.length
                ? externalSegments.map((s) => getSegments().find((x) => x.id === s)?.label || s).join(" ∪ ")
                : "لم تُختَر شريحة بعد"} (من اختيار الشرائح أعلى الصفحة)
            </div>
          ) : fixedSegment ? (
            <div data-testid="ab-segment-fixed" className="w-full text-xs p-2 rounded-lg border mb-3 bg-gray-50 text-gray-600">
              {getSegments().find((s) => s.id === fixedSegment)?.label || fixedSegment} (مثبَّتة لهذه البطاقة)
            </div>
          ) : (
            <select data-testid="ab-segment" value={segment} onChange={(e) => setSegment(e.target.value)}
                    className="w-full text-xs p-2 rounded-lg border mb-3">
              {getSegments().map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          )}

          <div className="text-xs font-bold mb-1" style={{ color: "#5B21B6" }}>الجنس:</div>
          <div className="flex gap-2 flex-wrap mb-3">
            {GENDERS.map((g) => {
              const on = gender === g.id;
              return (
                <button key={g.id || "none"} type="button" data-testid={`ab-gender-${g.id || "none"}`}
                        onClick={() => setGender(g.id)}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold border-2"
                        style={{ borderColor: on ? "#7C3AED" : "#e5e7eb",
                                 background: on ? "#EDE9FE" : "white",
                                 color: on ? "#5B21B6" : "#475569" }}>
                  {g.label}
                </button>
              );
            })}
          </div>

          <div className="text-xs font-bold mb-1" style={{ color: "#5B21B6" }}>نطاق العمر (اختياري):</div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs">من</span>
            <input data-testid="ab-age-min" type="number" min="0" max="120" value={ageMin}
                   onChange={(e) => setAgeMin(e.target.value)}
                   className="w-20 px-2 py-1 rounded border text-xs" placeholder="—" />
            <span className="text-xs">إلى</span>
            <input data-testid="ab-age-max" type="number" min="0" max="120" value={ageMax}
                   onChange={(e) => setAgeMax(e.target.value)}
                   className="w-20 px-2 py-1 rounded border text-xs" placeholder="—" />
            <span className="text-xs opacity-70">سنة</span>
          </div>
          <label className="flex items-center gap-2 cursor-pointer mb-3" data-testid="ab-include-unknown">
            <input type="checkbox" checked={includeUnknown}
                   onChange={(e) => setIncludeUnknown(e.target.checked)} />
            <span className="text-xs font-semibold" style={{ color: "#5B21B6" }}>
              شمل من لا بيانات جنس/عمر له (افتراضيًا يُستبعد عند تفعيل أي فلتر جنس/عمر)
            </span>
          </label>

          <div className="text-xs font-bold mb-1" style={{ color: "#5B21B6" }}>اشترى الصنف (اختياري):</div>
          <div className="flex gap-2 mb-3">
            <select data-testid="ab-product" value={productId} onChange={(e) => setProductId(e.target.value)}
                    className="flex-1 text-xs p-2 rounded-lg border">
              <option value="">بلا فلتر صنف</option>
              {options.products.map((p) => (
                <option key={p.product_id} value={p.product_id}>{p.name} ({p.invoices})</option>
              ))}
            </select>
            <input data-testid="ab-product-days" type="number" value={productDays} min={1}
                   onChange={(e) => setProductDays(e.target.value)}
                   className="w-20 text-xs p-2 rounded-lg border" />
            <InfoTip text="آخر N يوم" />
          </div>

          <div className="text-xs font-bold mb-1" style={{ color: "#5B21B6" }}>الفرع (اختياري):</div>
          <select data-testid="ab-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)}
                  className="w-full text-xs p-2 rounded-lg border mb-3">
            <option value="">كل الفروع</option>
            {options.branches.map((b) => (
              <option key={b.branch_id} value={b.branch_id}>{b.name} ({b.invoices})</option>
            ))}
          </select>

          <div className="text-xs font-bold mb-1" style={{ color: "#5B21B6" }}>عدد الزيارات ≥ (اختياري):</div>
          <input data-testid="ab-min-visits" type="number" min={1} value={minVisits}
                 onChange={(e) => setMinVisits(e.target.value)}
                 className="w-full text-xs p-2 rounded-lg border mb-3" />

          <div className="text-xs font-bold mb-1" style={{ color: "#5B21B6" }}>حالة جاهزة (اختياري):</div>
          <select data-testid="ab-metric-state" value={metricState}
                  onChange={(e) => setMetricState(e.target.value)}
                  className="w-full text-xs p-2 rounded-lg border mb-3">
            <option value="">بلا فلتر حالة</option>
            {metricStates.map((s) => <option key={s.key} value={s.key}>{s.label_ar}</option>)}
          </select>
          </>)}

          {audienceMode === "registered_no_visit" && (
            <div data-testid="ab-registered-no-visit-note" className="text-xs p-3 rounded-lg mb-3 bg-white border">
              عملاء سجّلوا (باركود الافتتاح أو ما شابه) ولم تصدر لهم أي فاتورة
              مدفوعة بعد — فئة مستقلة عن الشرائح، لا تتحد مع فلاتر الشريحة/
              الفرع أعلاه.
            </div>
          )}

          <button type="button" data-testid="ab-preview-btn" onClick={runPreview}
                  className="px-4 py-2 rounded-lg text-xs font-bold text-white mb-3"
                  style={{ background: "#7C3AED" }}>
            👁 معاينة العدّ والكلفة
          </button>

          {preview?.loading && <div className="text-xs" data-testid="ab-preview-loading">⏳ جارٍ الحساب…</div>}
          {preview?.error && <div className="text-xs text-red-700" data-testid="ab-preview-error">{preview.error}</div>}
          {preview?.data && (
            <div data-testid="ab-preview-result" className="rounded-lg p-3 bg-white border text-xs space-y-1">
              <div data-testid="ab-final-count"><b>العدد النهائي:</b> {preview.data.counts.final}</div>
              {audienceMode === "segment" && (<>
              <div data-testid="ab-coverage-gender"><b>الجنس:</b> {preview.data.coverage.gender_ar}</div>
              <div data-testid="ab-coverage-age"><b>العمر:</b> {preview.data.coverage.age_ar}</div>
              {preview.data.coverage.age_suspicious_ar && (
                <div data-testid="ab-coverage-age-suspicious" style={{ color: "#B45309" }}>
                  {preview.data.coverage.age_suspicious_ar}
                </div>
              )}
              {preview.data.coverage.minors_ar && (
                <div data-testid="ab-coverage-minors" style={{ color: "#B45309" }}>
                  {preview.data.coverage.minors_ar}
                </div>
              )}
              {preview.data.coverage.product_ar && (
                <div data-testid="ab-coverage-product"><b>الصنف:</b> {preview.data.coverage.product_ar}</div>
              )}
              </>)}
              <div data-testid="ab-exclusions">
                <b>استبعادات دائمة:</b>
                أرقام غير صالحة {preview.data.exclusions.invalid_phones_excluded} ·
                عدم تواصل {preview.data.exclusions.optout_excluded} ·
                تكرار {preview.data.exclusions.duplicates_excluded}
              </div>
              <div data-testid="ab-estimated-cost">
                <b>الكلفة المقدّرة:</b> {preview.data.estimated_cost.amount != null
                  ? `${preview.data.estimated_cost.amount} ${preview.data.estimated_cost.currency || ""}`
                  : "غير مُعَدّ السعر في الإعدادات"}
              </div>
              <button type="button" data-testid="ab-apply-btn" onClick={apply}
                      className="mt-2 px-4 py-2 rounded-lg text-xs font-bold text-white"
                      style={{ background: "#059669" }}>
                ✅ استخدام هذا الجمهور ({preview.data.counts.final})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AudienceBuilderPanel;
